/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import PropTypes from 'prop-types' // Npm: react.js library.
import _ from 'underscore' // Npm: underscore library.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useMutation, useQuery } from '@apollo/client' // Npm: Apollo client.
import { toast } from 'react-hot-toast' // Npm: React toastify.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import QuickFileUpload from 'components/QuickFileUpload'
import Modal from 'components/Modal'
import TableSpinner from 'components/TableSpinner'
import PhoneNumberoDropDownOptions from 'components/PhoneNumberoDropDownOptions'
import PhoneNumberoUpsert from 'components/PhoneNumberoUpsert'
import PhoneNumberoDelete from 'components/PhoneNumberoDelete'


/*
 * GRAPHS
 */
import PhoneNumberoReadQuery from './__query__/index.phoneNumbero.read.query'
import PhoneNumberoUploadMutation from './__mutation__/index.phoneNumbero.upload.mutation'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * OBJECTS
 */
const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [phoneNumberoToSearch, setPhoneNumberoToSearch] = React.useState('')
  const [MutationPhoneNumberoUpload] = useMutation(PhoneNumberoUploadMutation)
  const { 'isOpen': isPhoneNumberoCreateOpen, 'onOpen': onPhoneNumberoCreateOpen, 'onClose': onPhoneNumberoCreateClose } = useDisclosure()
  const { 'isOpen': isPhoneNumberoUpdateOpen, 'onOpen': onPhoneNumberoUpdateOpen, 'onClose': onPhoneNumberoUpdateClose } = useDisclosure()
  const { 'isOpen': isPhoneNumberoDeleteOpen, 'onOpen': onPhoneNumberoDeleteOpen, 'onClose': onPhoneNumberoDeleteClose } = useDisclosure()
  const _QueryPhoneNumberoRead = useQuery(PhoneNumberoReadQuery, {
    'variables': {
      'phoneNumberoDirectoryId': passOn?.phoneNumberoDirectoryId,
      phoneNumberoToSearch,
      'skip': paginationModel?.pageSize * paginationModel?.page,
      'take': paginationModel?.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })
  const _isFirstLoadCompleted = React.useRef(false)
  const _onSearchInputChange = React.useRef(_.debounce((value) => setPhoneNumberoToSearch(value), 800)).current
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data Assignment.
  if (!_QueryPhoneNumberoRead.loading && 0 < _QueryPhoneNumberoRead.data?.PhoneNumberoRead?.length && !_isFirstLoadCompleted.current) _isFirstLoadCompleted.current = true
  if (!_.every(_.pluck(_QueryPhoneNumberoRead.data?.PhoneNumberoRead, 'status'), i => _successFlags.includes(i))) _isFirstLoadCompleted.current = false

  // Extract search from filter model
  React.useEffect(() => {
    const phoneNumberoFilter = filterModel?.items?.find(item => item.field === 'phoneNumbero')
    const searchValue = phoneNumberoFilter?.value || ''
    _onSearchInputChange(searchValue)
  }, [filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
    }
  }, [])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 230
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'phoneNumbero',
      'headerName': 'PhoneNumbero',
      'description': 'Phone number.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 330
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Created date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 330
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Updated date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 330
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for PhoneNumbero.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 210,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <PhoneNumberoDropDownOptions
        phoneNumberoId={row?.id}
        phoneNumberoDirectoryId={passOn?.phoneNumberoDirectoryId}
        onPhoneNumberoUpdate={onPhoneNumberoUpdateOpen}
        onPhoneNumberoDelete={onPhoneNumberoDeleteOpen}
      />
    }
  ], [onPhoneNumberoUpdateOpen, onPhoneNumberoDeleteOpen, passOn?.phoneNumberoDirectoryId])

  const rows = React.useMemo(() => _.compact(_QueryPhoneNumberoRead?.data?.PhoneNumberoRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id || String.random(9),
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'phoneNumbero': r?.phoneNumbero ?? '-',
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD hh:mm:ss A'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD hh:mm:ss A')
  }) : null)), [_QueryPhoneNumberoRead?.data?.PhoneNumberoRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='phoneNumbero base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          minH="81vh"
          maxH='81vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between' gap='12px' flexWrap={{ 'base': 'wrap', 'md': 'nowrap' }}>
            <Tooltip label='Create New PhoneNumbero' fontSize='sm'>
              <Button
                leftIcon={<IoMdAdd />}
                onClick={onPhoneNumberoCreateOpen}
                style={buttonStyle}>
                Create PhoneNumbero
              </Button>
            </Tooltip>
            <Flex gap='12px'>
              <QuickFileUpload
                onChange={j => {
                  // Const assignment.
                  const _phoneNumberos = Array.from({ 'length': j?.rows?.length }, (__, i) => _.first(j?.rows[i]))

                  // Execute mutation.
                  return MutationPhoneNumberoUpload({ 'variables': { 'phoneNumberoDirectoryId': passOn?.phoneNumberoDirectoryId, 'phoneNumberos': _phoneNumberos } }).then(jp => {
                    // On Successful response from the mutation.
                    if ('UPSERT_SUCCESSFUL' === jp?.data?.PhoneNumberoUpload?.status) {
                      // Toast success.
                      toast('Successfully uploaded the phoneNumberos in your phoneNumbero directory.')
                    } else {
                      // Toast failure.
                      toast(jp?.data?.PhoneNumberoUpload?.message ?? 'Failed to upload the phoneNumberos in your phoneNumbero directory.')
                    }
                  })
                }}
                isLoading={false}
              />
              <DownloadToExcel
                cellsData={0 === _QueryPhoneNumberoRead?.data?.PhoneNumberoRead?.length || !_.every(_.pluck(_QueryPhoneNumberoRead?.data?.PhoneNumberoRead, 'status'), i => _successFlags.includes(i)) ? [] : _QueryPhoneNumberoRead?.data?.PhoneNumberoRead.map((item, __index) => ({
                  'S.No.': `${(__index + (paginationModel.pageSize * (paginationModel.page))) + 1}.`,
                  'CreatedAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
                  'UpdatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD'),
                  'PhoneNumbero': item.phoneNumbero ?? '-'
                }))}
                headersData={['S.No.', 'CreatedAt', 'UpdatedAt', 'PhoneNumbero'].map(i => ({ 'key': i, 'label': i }))} />
            </Flex>
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QueryPhoneNumberoRead?.data?.PhoneNumberoRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryPhoneNumberoRead?.loading}
          />
        </Flex>
        <Modal
          size='md'
          title='PhoneNumbero Create'
          isOpen={isPhoneNumberoCreateOpen}
          onClose={onPhoneNumberoCreateClose}>
          <PhoneNumberoUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='md'
          title='PhoneNumbero Update'
          isOpen={isPhoneNumberoUpdateOpen}
          onClose={onPhoneNumberoUpdateClose}>
          <PhoneNumberoUpsert />
        </Modal>
        <Modal
          size='md'
          title='PhoneNumbero Delete'
          isOpen={isPhoneNumberoDeleteOpen}
          onClose={onPhoneNumberoDeleteClose}>
          <PhoneNumberoDelete />
        </Modal>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'passOn': PropTypes.object
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
